import { useQuery } from "react-query";
import {
  useAuthorizedInfiniteQuery,
  useAuthorizedMutation,
  useAuthorizedQuery,
} from "..";
import { params } from "../../params";
import { buildFilterSearchParams } from "../../routes/explore/context";
import {
  FilterValue,
  pasrseFilterValue,
} from "../../routes/explore/context/FilterContext.model";
import { apiResolver, useApi } from "../useApi";
import {
  ExploreRequest,
  ExploreResponse,
  ExploreValuesResponse,
  TicketExploreResponse,
  translateColumnsToTicketExploreRows,
} from "./Explore.model";
import {
  ConversationResponse,
  ConversationSources,
  ExploreConversationRequest,
  ExploreConversationResponse,
} from "./ExploreConversation.model";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";

export const useExplore = () => {
  return useAuthorizedMutation<ExploreResponse, ExploreRequest>({
    url: `${params.API_URL}/platform/explorer/explore`,
    method: "POST",
  });
};

export const useExploreTickets = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/explorer/tickets`,
    method: "POST",
  });
};

export const useExploreValues = (enabled: boolean) => {
  return useAuthorizedQuery<ExploreValuesResponse>({
    queryKey: `useExploreValues`,
    url: `${params.API_URL}/platform/explorer/values`,
    method: "GET",
    enabled,
  });
};

export const useExploreInfiniteTickets = (
  {
    includeComments,
    ...filterValue
  }: FilterValue & { includeComments: boolean },
  enabled: boolean
) => {
  const searchParams = buildFilterSearchParams(filterValue);

  return useAuthorizedInfiniteQuery<TicketExploreResponse>({
    url: `${params.API_URL}/platform/explorer/tickets/`,
    method: "POST",
    queryKey: `useExploreInfiniteTickets_${searchParams.toString()}`,
    enabled,
    body: { ...pasrseFilterValue(filterValue), includeComments },
    pageKey: "page",
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.metadata?.isThereNextPage && allPages.length + 1;
    },
  });
};

export const getTicketsFormExplorerObject = ({
  dateFrom,
  dateTo,
  page = 1,
  topicName,
  channel,
  tenantGroup,
  customField,
  workspace,
}: {
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  topicName: string;
  channel?: string;
  tenantGroup?: string[];
  customField?: boolean;
  workspace?: string;
}) => {
  const now = new Date();
  const lastMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    now.getDate()
  );
  return {
    page,
    groupBy: "topics",
    dateFrom: dateFrom || lastMonth.toISOString(),
    dateTo: dateTo || now.toISOString(),
    stringSliceBy: {
      and: [
        {
          type: "topic",
          operation: "and",
          values: [
            {
              type: "topic",
              operation: "eq",
              value: topicName,
            },
          ],
        },
        {
          type: "channel",
          operation: "or",
          values: [
            {
              type: "channel",
              operation: "eq",
              value: "api",
            },
            {
              type: "channel",
              operation: "eq",
              value: "email",
            },
            {
              type: "channel",
              operation: "eq",
              value: "web",
            },
          ],
        },
        ...(tenantGroup && tenantGroup.length > 0
          ? [
              {
                type: "tenantGroup",
                operation: "or",
                values: tenantGroup.map((group) => ({
                  type: "tenantGroup",
                  operation: "eq",
                  value: group,
                })),
              },
            ]
          : []),
        // TODO : make it generic
        ...(workspace === "notable" ? [{ partner: { in: ["Compass"] } }] : []),
      ],
    },
    includeComments: true,
    includeCustomField: customField,
  };
};
export const getOpenTicketsFormExplorerObjectWithoutTopic = ({
  dateFrom,
  dateTo,
  page = 1,
}: {
  dateFrom?: string;
  dateTo?: string;
  page?: number;
}) => {
  const now = new Date();
  const lastMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    now.getDate()
  );
  return {
    page,
    groupBy: "topics",
    dateFrom: dateFrom || lastMonth.toISOString(),
    dateTo: dateTo || now.toISOString(),
    stringSliceBy: {
      and: [
        {
          type: "status",
          operation: "or",
          values: [
            {
              type: "status",
              operation: "eq",
              value: "Escalated to T2",
            },
            {
              type: "status",
              operation: "eq",
              value: "Open",
            },
            {
              type: "status",
              operation: "eq",
              value: "T1",
            },
            {
              type: "status",
              operation: "eq",
              value: "open",
            },
            {
              type: "status",
              operation: "eq",
              value: "new",
            },
            {
              type: "status",
              operation: "eq",
              value: "New",
            },
            {
              type: "status",
              operation: "eq",
              value: "Pending agent",
            },
            {
              type: "status",
              operation: "eq",
              value: "Pending Yotpo",
            },
          ],
        },
      ],
    },
    includeComments: true,
  };
};

export const useExploreConversations = () => {
  return useAuthorizedMutation<
    ExploreConversationResponse,
    ExploreConversationRequest
  >({
    url: `${params.API_URL}/platform/explorer/explore-conversations`,
    method: "POST",
  });
};

export const useGetConversationsSources = (chatSessionId: string) => {
  return useAuthorizedQuery<ConversationSources[]>({
    queryKey: `useGetConversationsSources_${chatSessionId}`,
    url: `${params.API_URL}/sessions-api/chat-session/sessions/${chatSessionId}/sources`,
    method: "GET",
    enabled: !!chatSessionId,
  });
};

export const useInfiniteConversations = (
  {
    includeMessages,
    ...filterValue
  }: FilterValue & { includeMessages: boolean },
  enabled: boolean
) => {
  const searchParams = buildFilterSearchParams(filterValue);

  return useAuthorizedInfiniteQuery<ConversationResponse>({
    url: `${params.API_URL}/platform/explorer/conversations/`,
    method: "POST",
    queryKey: `useInfiniteConversations_${searchParams.toString()}`,
    enabled,
    body: { ...pasrseFilterValue(filterValue), includeMessages },
    pageKey: "page",
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.metadata?.isThereNextPage && allPages.length + 1;
    },
  });
};

export const filterCustomField = (
  tickets: any & { metadata: Array<{ id: number; value: string }> },
  customField: string[]
) => {
  if (!customField || customField.length === 0) return tickets;
  return tickets.filter((ticket: any) => {
    if (!ticket.metadata || ticket.metadata.length === 0) return false;
    const domain = ticket.metadata.find(
      (metadata: { id: number; value: string }) => metadata.id === 7776207191197
    );
    return domain && customField.includes(domain.value);
  });
};

export function useExploreGetTicket(ticketId: string) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["ticketInfo", ticketId],
    queryFn: async () => {
      const response = await apiResolver<TicketExploreResponse>(
        api.post("/platform/explorer/tickets", {
          page: 1,
          dateFrom: "2023-01-01T00:00:00+02:00",
          stringSliceBy: {
            and: [
              {
                type: "ticket-id",
                operation: "or",
                values: [
                  {
                    type: "ticket-id",
                    operation: "eq",
                    value: ticketId,
                  },
                ],
              },
            ],
          },
          includeComments: true,
        })
      );

      const rows = translateColumnsToTicketExploreRows(response);
      const ticket = rows.find((row) => row.overview.id === ticketId);
      if (!ticket) {
        throw new Error("Ticket not found");
      }

      return ticket;
    },
  });
}
